/**
 * Operaciones PURAS sobre la definición del reporte (§4.3). Los componentes las
 * aplican con `setDef((d) => ops.xxx(d, ...))`; nada muta en sitio. Las tablas
 * llevan un alias `t1, t2…` (la primera es el hub/base · RN-11) y las columnas/
 * filtros/orden referencian ese alias.
 */
import type {
  Catalogo,
  RepColumnaCalc,
  RepColumnaSel,
  RepDefinicion,
  RepFiltro,
  RepJoin,
  RepOrden,
} from "../services/reporteria.api";

export function emptyDef(): RepDefinicion {
  return { tablas: [], columnas: [], joins: [], filtros: [], orden: [], columnasCalculadas: [], agrupar: false };
}

export function baseAlias(def: RepDefinicion): string | null {
  return def.tablas[0]?.alias ?? null;
}

/**
 * Rellena la etiqueta legible de cada columna desde el catálogo cuando viene
 * vacía (p. ej. un predefinido guardado que no round-trippeó la etiqueta): así
 * ningún control muestra el token opaco en vez del nombre. Devuelve el mismo
 * objeto si no hubo nada que rellenar (evita renders extra).
 */
export function hidratarEtiquetas(def: RepDefinicion, catalogo: Catalogo): RepDefinicion {
  const etiquetaPorToken = new Map<string, string>();
  for (const t of catalogo.tablas) for (const c of t.columnas) etiquetaPorToken.set(c.token, c.etiqueta);

  let cambio = false;
  const columnas = def.columnas.map((c) => {
    if (c.etiqueta && c.etiqueta.trim() !== "") return c;
    const etiqueta = etiquetaPorToken.get(c.token);
    if (!etiqueta) return c;
    cambio = true;
    return { ...c, etiqueta };
  });
  return cambio ? { ...def, columnas } : def;
}

export function aliasDeToken(def: RepDefinicion, token: string): string | null {
  return def.tablas.find((t) => t.token === token)?.alias ?? null;
}

export function addTabla(def: RepDefinicion, token: string): RepDefinicion {
  if (def.tablas.some((t) => t.token === token)) return def;
  const alias = `t${def.tablas.length + 1}`;
  return { ...def, tablas: [...def.tablas, { token, alias }] };
}

export function removeTabla(def: RepDefinicion, alias: string): RepDefinicion {
  return {
    ...def,
    tablas: def.tablas.filter((t) => t.alias !== alias),
    columnas: def.columnas.filter((c) => c.tablaAlias !== alias),
    filtros: (def.filtros ?? []).filter((f) => f.tablaAlias !== alias),
    orden: (def.orden ?? []).filter((o) => o.tablaAlias !== alias),
    joins: (def.joins ?? []).filter((j) => j.tablaIzqAlias !== alias && j.tablaDerAlias !== alias),
  };
}

export function hasColumna(def: RepDefinicion, tablaAlias: string, token: string): boolean {
  return def.columnas.some((c) => c.tablaAlias === tablaAlias && c.token === token);
}

export function addColumna(def: RepDefinicion, tablaAlias: string, token: string, etiqueta: string): RepDefinicion {
  if (hasColumna(def, tablaAlias, token)) return def;
  return { ...def, columnas: [...def.columnas, { tablaAlias, token, etiqueta }] };
}

export function removeColumna(def: RepDefinicion, index: number): RepDefinicion {
  return { ...def, columnas: def.columnas.filter((_, i) => i !== index) };
}

export function updateColumna(def: RepDefinicion, index: number, patch: Partial<RepColumnaSel>): RepDefinicion {
  return { ...def, columnas: def.columnas.map((c, i) => (i === index ? { ...c, ...patch } : c)) };
}

export function moveColumna(def: RepDefinicion, index: number, dir: -1 | 1): RepDefinicion {
  const j = index + dir;
  if (j < 0 || j >= def.columnas.length) return def;
  const cols = [...def.columnas];
  [cols[index], cols[j]] = [cols[j], cols[index]];
  return { ...def, columnas: cols };
}

/** Mueve la columna de la posición `desde` a la posición `hasta` (drag-and-drop). */
export function reordenarColumna(def: RepDefinicion, desde: number, hasta: number): RepDefinicion {
  if (desde === hasta || desde < 0 || hasta < 0 || desde >= def.columnas.length || hasta >= def.columnas.length) return def;
  const cols = [...def.columnas];
  const [m] = cols.splice(desde, 1);
  cols.splice(hasta, 0, m);
  return { ...def, columnas: cols };
}

export function setAgrupar(def: RepDefinicion, agrupar: boolean): RepDefinicion {
  return { ...def, agrupar };
}

export function setLimite(def: RepDefinicion, limite: number | null): RepDefinicion {
  return { ...def, limite };
}

export function setFiltroFormula(def: RepDefinicion, filtroFormula: string | null): RepDefinicion {
  return { ...def, filtroFormula: filtroFormula && filtroFormula.trim() !== "" ? filtroFormula : null };
}

// ---- Filtros ----

export function addFiltro(def: RepDefinicion, filtro: RepFiltro): RepDefinicion {
  return { ...def, filtros: [...(def.filtros ?? []), filtro] };
}
export function updateFiltro(def: RepDefinicion, index: number, patch: Partial<RepFiltro>): RepDefinicion {
  return { ...def, filtros: (def.filtros ?? []).map((f, i) => (i === index ? { ...f, ...patch } : f)) };
}
export function removeFiltro(def: RepDefinicion, index: number): RepDefinicion {
  return { ...def, filtros: (def.filtros ?? []).filter((_, i) => i !== index) };
}

// ---- Orden ----

export function addOrden(def: RepDefinicion, orden: RepOrden): RepDefinicion {
  return { ...def, orden: [...(def.orden ?? []), orden] };
}

/** Dirección de orden aplicada a una columna (por su alias+token de salida), o null. */
export function ordenDe(def: RepDefinicion, tablaAlias: string, token: string): "asc" | "desc" | null {
  const o = (def.orden ?? []).find((x) => x.tablaAlias === tablaAlias && x.token === token);
  return o ? (o.direccion === "desc" ? "desc" : "asc") : null;
}

/** Cicla el orden de una columna al hacer clic en su encabezado: (ninguno)→asc→desc→(ninguno). */
export function ciclarOrden(def: RepDefinicion, tablaAlias: string, token: string): RepDefinicion {
  const orden = def.orden ?? [];
  const idx = orden.findIndex((o) => o.tablaAlias === tablaAlias && o.token === token);
  if (idx < 0) return { ...def, orden: [...orden, { tablaAlias, token, direccion: "asc" }] };
  if (orden[idx].direccion !== "desc")
    return { ...def, orden: orden.map((o, i) => (i === idx ? { ...o, direccion: "desc" } : o)) };
  return { ...def, orden: orden.filter((_, i) => i !== idx) };
}
export function updateOrden(def: RepDefinicion, index: number, patch: Partial<RepOrden>): RepDefinicion {
  return { ...def, orden: (def.orden ?? []).map((o, i) => (i === index ? { ...o, ...patch } : o)) };
}

/**
 * Fija (o quita, con `null`) el orden de una columna en una dirección EXPLÍCITA.
 * A diferencia de `ciclarOrden`, no cicla: es la acción de un menú "Ordenar
 * ascendente / descendente / quitar orden".
 */
export function fijarOrden(
  def: RepDefinicion,
  tablaAlias: string,
  token: string,
  direccion: "asc" | "desc" | null
): RepDefinicion {
  const orden = (def.orden ?? []).filter((o) => !(o.tablaAlias === tablaAlias && o.token === token));
  return { ...def, orden: direccion ? [...orden, { tablaAlias, token, direccion }] : orden };
}
export function removeOrden(def: RepDefinicion, index: number): RepDefinicion {
  return { ...def, orden: (def.orden ?? []).filter((_, i) => i !== index) };
}

// ---- Joins (relaciones sugeridas) ----

export function tieneJoin(def: RepDefinicion, rid: string): boolean {
  return (def.joins ?? []).some((j) => j.rid === rid);
}

export function toggleJoin(def: RepDefinicion, join: RepJoin): RepDefinicion {
  const joins = def.joins ?? [];
  return tieneJoin(def, join.rid ?? "")
    ? { ...def, joins: joins.filter((j) => j.rid !== join.rid) }
    : { ...def, joins: [...joins, join] };
}

/** Cambia el tipo de una relación activa (inner/left/right) por su rid. */
export function setJoinTipo(def: RepDefinicion, rid: string, tipo: string): RepDefinicion {
  return { ...def, joins: (def.joins ?? []).map((j) => (j.rid === rid ? { ...j, tipo } : j)) };
}

/** Quita una relación activa por su rid. */
export function removeJoin(def: RepDefinicion, rid: string): RepDefinicion {
  return { ...def, joins: (def.joins ?? []).filter((j) => j.rid !== rid) };
}

// ---- Columnas calculadas ----

export function addCalc(def: RepDefinicion, calc: RepColumnaCalc): RepDefinicion {
  return { ...def, columnasCalculadas: [...(def.columnasCalculadas ?? []), calc] };
}
export function updateCalc(def: RepDefinicion, index: number, patch: Partial<RepColumnaCalc>): RepDefinicion {
  return {
    ...def,
    columnasCalculadas: (def.columnasCalculadas ?? []).map((c, i) => (i === index ? { ...c, ...patch } : c)),
  };
}
export function removeCalc(def: RepDefinicion, index: number): RepDefinicion {
  return { ...def, columnasCalculadas: (def.columnasCalculadas ?? []).filter((_, i) => i !== index) };
}

/** ¿La definición tiene lo mínimo para ejecutarse? (al menos 1 tabla y 1 columna) */
export function esEjecutable(def: RepDefinicion): boolean {
  return def.tablas.length > 0 && def.columnas.length > 0;
}

/** Firma estable para cachear el conteo (cambia si cambia cualquier parte de la def). */
export function firmaDef(def: RepDefinicion): string {
  return JSON.stringify(def);
}
